import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser'
import styled from 'styled-components'
import ButtonDefault from 'components/elements/ButtonDefault'
import ButtonWhite from 'components/elements/ButtonWhite'
import CustomLink from 'components/shared/CustomLink'

const Content = styled.div`
  font-weight: ${(props) => props.theme.font.weight.s};
  font-size: ${(props) => props.theme.font.size[16]};
  color: ${(props) => props.theme.color.text.main};

  & strong,
  & b {
    font-weight: ${(props) => props.theme.font.weight.l};
    color: ${(props) => props.theme.color.text.secondary};
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    text-align: initial;
    font-weight: ${(props) => props.theme.font.weight.l};
    color: ${(props) => props.theme.color.text.secondary};

    & strong,
    & b {
      font-weight: ${(props) => props.theme.font.weight.l};
    }

    @media (min-width: 992px) {
      font-size: ${(props) => props.theme.font.size[25]};
      line-height: ${(props) => props.theme.font.size[25]};
    }

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size[18]};
      line-height: ${(props) => props.theme.font.size[18]};
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  & ul {
    & li {
    }
  }

  & p {
    &:last-child {
      margin-bottom: 0;
    }

    & a {
      font-weight: ${(props) => props.theme.font.weight.l};
      color: ${(props) => props.theme.color.text.contrast};
    }
  }

  & a {
  }

  & img {
  }

  /*
    WP WYSIWYG
  */
  .alignright {
    float: right;
    text-align: right;
  }

  .aligncenter {
    display: block;
    margin: 0 auto;
    text-align: center;
  }

  .wp-caption {
    max-width: 100%;
  }

  .wp-caption-text {
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }
`

const StrongUnderlineWrap = styled.span`
  position: relative;

  & strong {
    font-weight: 300;
  }

  & > svg {
    position: absolute;
    bottom: -1.5rem;
    left: 1rem;
  }
`

function StrongUnderlineSvg() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="191"
      height="20"
      viewBox="0 0 191 20"
      fill="none"
      aria-hidden
    >
      <path
        d="M0.713615 20.0001L0 17.2616C30.8063 12.0998 62.4355 8.00126 94.0088 5.08175C114.455 3.192 138.155 1.49067 155.861 0.640001C165.937 0.156837 173.927 -0.0558297 179.61 0.0131937C187.911 0.112065 190.675 0.707159 190.979 2.46819C191.267 4.11915 188.841 5.03698 186.544 5.77012C183.562 6.71966 178.62 7.89492 171.858 9.26233L171.007 6.5499C179.292 4.87468 183.492 3.76098 185.619 3.06328C182.16 2.74428 173.776 2.53162 153.52 3.56137C136.049 4.44935 113.953 6.05741 94.411 7.86321C62.9399 10.7715 31.4176 14.8551 0.713615 20.0001Z"
        fill="#FF6700"
      />
    </svg>
  )
}

const capitalize = (s: string) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const convertCSSKey = (key: string) => {
  const keySplit = key.split('-')
  let newKey = ''

  keySplit.forEach((value, index) => {
    let newValue = value

    if (index > 0) {
      newValue = capitalize(value)
    }

    newKey += newValue
  })

  return newKey
}

const convertCSS = (style: any) => {
  const result: any = []

  if (!style) {
    return result
  }

  const attributes = style.split(';')

  attributes.forEach((attribute: any) => {
    const { 0: key, 1: value } = attribute.split(':')

    result[convertCSSKey(key)] = value
  })

  return result
}

const findAndReplace = (content: any, settings: any) => {
  const parseOptions: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode.name === 'a' && domNode.attribs.class === 'default-button') {
        return (
          <ButtonDefault to={domNode.attribs.href}>
            {domToReact(domNode.children, parseOptions)}
          </ButtonDefault>
        )
      }

      if (domNode.name === 'a' && domNode.attribs.class === 'white-button') {
        return (
          <ButtonWhite to={domNode.attribs.href}>
            {domToReact(domNode.children, parseOptions)}
          </ButtonWhite>
        )
      }

      if (settings.strongUnderlineSvg && domNode.name === 'strong') {
        return (
          <StrongUnderlineWrap>
            <strong>{domToReact(domNode.children || [], parseOptions)}</strong>
            <StrongUnderlineSvg />
          </StrongUnderlineWrap>
        )
      }

      if (domNode.children && domNode.children.length > 0) {
        const htmlFindAndReplace = domToReact(domNode.children, parseOptions)

        const attributes = domNode.attribs

        if (
          attributes.src &&
          attributes.src.indexOf(settings.sourceUrl) === -1
        ) {
          attributes.src = `${settings.sourceUrl}${attributes.src}`
        }

        if (attributes.href) {
          if (
            attributes.href.indexOf('www.') !== -1 ||
            attributes.href.indexOf('http:') !== -1 ||
            attributes.href.indexOf('https:') !== -1
          ) {
            attributes.target = '_blank'
          } else if (
            attributes.href.indexOf('/wp-content/') !== -1 &&
            attributes.href.indexOf(settings.sourceUrl) === -1
          ) {
            attributes.href = `${settings.sourceUrl}${attributes.href}`
          }
        }

        attributes.className = attributes.class

        delete attributes.class

        if (attributes.style) {
          attributes.style = convertCSS(attributes.style)
        }

        const Comp = domNode.name

        return (
          <Comp
            src={attributes.src}
            className={attributes.className}
            style={attributes.style}
            id={attributes.id}
            href={attributes.href}
            target={attributes.target}
          >
            {htmlFindAndReplace}
          </Comp>
        )
      }

      if (domNode.name === 'img') {
        const attributes = domNode.attribs

        if (
          domNode.attribs.src.indexOf('/wp-content/') !== -1 &&
          domNode.attribs.src.indexOf(settings.sourceUrl) === -1
        ) {
          attributes.src = `${settings.sourceUrl}${domNode.attribs.src}`
        }

        attributes.className = attributes.class

        delete attributes.class

        if (attributes.style) {
          attributes.style = convertCSS(attributes.style)
        }

        return (
          <img
            src={attributes.src}
            className={attributes.className}
            style={attributes.style}
            width={attributes.width}
            height={attributes.height}
            alt={attributes.alt}
          />
        )
      }

      if (domNode.name === 'a') {
        const attributes = domNode.attribs

        if (attributes.style) {
          delete attributes.style
        }

        if (
          attributes.href.indexOf('www.') !== -1 ||
          attributes.href.indexOf('http:') !== -1 ||
          attributes.href.indexOf('https:') !== -1
        ) {
          return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <a {...attributes} target="_blank">
              {domToReact(domNode.children, parseOptions)}
            </a>
          )
        }

        return (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <CustomLink to={attributes.href} {...attributes}>
            {domToReact(domNode.children, parseOptions)}
          </CustomLink>
        )
      }

      return null
    },
  }

  return parse(content, parseOptions)
}

interface ParseContentProps {
  content: any
  className?: string
  /** When true, each &lt;strong&gt; is rendered with a decorative SVG underline (opt-in). */
  strongUnderlineSvg?: boolean
}

function ParseContent({
  content,
  className = '',
  strongUnderlineSvg = false,
}: ParseContentProps) {
  // eslint-disable-next-line
  const { site } = useStaticQuery<GatsbyTypes.ParseContentQueryQuery>(graphql`
    query ParseContentQuery {
      site {
        siteMetadata {
          siteWpUrl
        }
      }
    }
  `)

  let sourceUrl = ''

  if (site && site.siteMetadata && site.siteMetadata.siteWpUrl) {
    sourceUrl = site.siteMetadata.siteWpUrl
  }

  if (!content) {
    return null
  }

  return (
    <Content className={className}>
      {findAndReplace(content, {
        sourceUrl,
        strongUnderlineSvg,
      })}
    </Content>
  )
}

export default ParseContent
